'use client'

import { Cell, DoubleImage, Image, Radio, Spacer, Text } from '@vinted/web-ui'
import { ChangeEvent, useEffect, useRef, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useIsMounted } from 'usehooks-ts'

import { useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import {
  InputDropdown,
  InputDropdownRenderProps,
} from '@marketplace-web/common-components/input-dropdown-ui'
import { useDebounce } from '@marketplace-web/shared/ui-helpers'
import { List } from '@marketplace-web/common-components/list-ui'
import {
  renderValidation,
  useFormValidationMessage,
} from '@marketplace-web/vendor-abstractions/react-hook-form-feature'
import { EMPTY_USER_IMAGE_NAME, useAsset } from '@marketplace-web/shared/assets'
import { findThumbnail } from '@marketplace-web/shared/utils'
import {
  TransactionDto,
  TransactionModel,
  transformTransaction,
  transformTransactions,
  TransactionSide,
} from '@marketplace-web/messaging/conversation-messaging-data'
import { CurrencyAmountModel, formatCurrencyAmount } from '@marketplace-web/currency/currency-data'

import { getRecentTransactions } from '@marketplace-web/help-center/help-center-transactions-data'

import { Field } from './types'

const DEBOUNCE_DELAY = 350
export const EMPTY_ITEM_IMAGE = 'item.svg'

enum ItemThumbnailSize {
  Small = 'thumb70x100',
  Medium = 'thumb150x210',
}

const getItemPhotoThumbnail = (
  photos:
    | Array<{
        isMain: boolean
        thumbnails: Array<{ type: string; url: string }>
      }>
    | undefined,
  size = ItemThumbnailSize.Medium,
) => {
  const photo = photos?.find(({ isMain }) => isMain)

  return findThumbnail(photo, size)
}

type Props = {
  preselectedTransaction: TransactionDto | null
  isTransactionRequired?: boolean
}

const TransactionSelect = ({ isTransactionRequired, preselectedTransaction }: Props) => {
  const { locale } = useIntl()
  const breakpoints = useBreakpoint()
  const translate = useTranslate('help_contact_form.transaction')
  const inputDropdownTranslate = useTranslate()
  const isMounted = useIsMounted()
  const asset = useAsset('/assets/no-photo')

  const {
    setValue,
    control,
    clearErrors,
    formState: { errors },
  } = useFormContext()

  const validationMessage = useFormValidationMessage(errors, 'help_contact_form')

  const transformedPreselectedTransaction = preselectedTransaction
    ? transformTransaction(preselectedTransaction)
    : null

  const [recentTransactions, setRecentTransactions] = useState<Array<TransactionModel> | null>(null)
  const [selectedValue, setSelectedValue] = useState<TransactionModel | null | undefined>(
    transformedPreselectedTransaction,
  )
  const [searchTerm, setSearchTerm] = useState('')
  const prevValueRef = useRef(selectedValue)
  const prevValue = prevValueRef.current

  const setSearchTermDebounced = useDebounce((value: string) => {
    if (!isMounted()) return

    setSearchTerm(value)
  }, DEBOUNCE_DELAY)

  function handleTransactionSearch({ target }: ChangeEvent<HTMLInputElement>) {
    setSearchTermDebounced(target.value)
  }

  const handleItemSelect =
    (transaction: TransactionModel, inputRenderProps: InputDropdownRenderProps) => () => {
      inputRenderProps.setIsSaveEnabled(true)
      clearErrors(Field.RelatedTransaction)
      setSelectedValue(transaction)

      if (breakpoints.phones) return

      inputRenderProps.closeDropdown()
      setValue(Field.RelatedTransaction, transaction.id)
    }

  useEffect(() => {
    prevValueRef.current = selectedValue
  }, [selectedValue])

  useEffect(() => {
    async function fetchRecentTransactions() {
      const response = await getRecentTransactions()

      if ('errors' in response) return

      const transactions = transformTransactions(response.recent_transactions)
      const searchResults = transactions?.filter(({ itemTitle }) =>
        itemTitle.toLowerCase().includes(searchTerm.toLowerCase()),
      )

      setRecentTransactions(searchResults)
    }

    fetchRecentTransactions()
  }, [searchTerm])

  function handleSave() {
    setValue(Field.RelatedTransaction, selectedValue?.id)
  }

  function handleCancel() {
    setSelectedValue(prevValue)
  }

  function renderTransactionInfo(price: CurrencyAmountModel | null, transaction: TransactionModel) {
    const priceLabel = price ? formatCurrencyAmount(price, locale) : null

    const latestStatusTitle =
      transaction.statusUpdatedAt &&
      transaction.shipment?.statusUpdatedAt &&
      new Date(transaction.statusUpdatedAt) < new Date(transaction.shipment.statusUpdatedAt)
        ? transaction?.shipment?.statusTitle
        : transaction.statusTitle

    if (!price && !latestStatusTitle) return null

    return (
      <div className="u-flexbox u-flex-direction-column">
        {priceLabel && (
          <>
            <Text as="span" text={priceLabel} />
            <Spacer size={Spacer.Size.Small} />
          </>
        )}
        <Text as="span" text={latestStatusTitle} />
      </div>
    )
  }

  function renderPrefix(transaction: TransactionModel) {
    const item = transaction.order.items[0]
    const oppositeUser =
      transaction.userSide === TransactionSide.Seller ? transaction.buyer : transaction.seller
    const itemImageSrc =
      getItemPhotoThumbnail(item?.photos, ItemThumbnailSize.Small) || asset(EMPTY_ITEM_IMAGE)

    return (
      <DoubleImage
        primary={
          <Image
            alt={`transaction-image-${transaction.id}`}
            src={itemImageSrc}
            size={Image.Size.Large}
          />
        }
        secondary={
          <Image
            styling={Image.Styling.Circle}
            src={findThumbnail(oppositeUser?.photo) || asset(EMPTY_USER_IMAGE_NAME)}
            size={Image.Size.Regular}
          />
        }
      />
    )
  }

  function renderTransaction(
    transaction: TransactionModel,
    inputRenderProps: InputDropdownRenderProps,
  ) {
    const isSelected = selectedValue?.id === transaction.id
    const labelId = `radio-label-${transaction.id}`

    return (
      <Cell
        key={transaction.id}
        title={transaction.itemTitle}
        body={renderTransactionInfo(transaction.offer.price, transaction)}
        onClick={handleItemSelect(transaction, inputRenderProps)}
        suffix={
          <Radio
            name={`${transaction.id}-radio`}
            checked={isSelected}
            onChange={() => undefined}
            aria={{
              'aria-labelledby': labelId,
            }}
          />
        }
        prefix={renderPrefix(transaction)}
        clickable
      />
    )
  }

  function renderTransactionList(inputRenderProps: InputDropdownRenderProps) {
    return (
      <List>
        {recentTransactions?.map(transaction => renderTransaction(transaction, inputRenderProps))}
      </List>
    )
  }

  return (
    <Controller
      defaultValue={selectedValue?.id || null}
      name={Field.RelatedTransaction}
      control={control}
      rules={{ required: isTransactionRequired }}
      render={() => (
        <InputDropdown
          isPhone={!!breakpoints.phones}
          textSave={inputDropdownTranslate('actions.save')}
          name={Field.RelatedTransaction}
          title={translate('title')}
          placeholder={translate('placeholder')}
          render={renderTransactionList}
          onChange={handleTransactionSearch}
          value={selectedValue?.itemTitle}
          onSave={handleSave}
          onCancel={handleCancel}
          disabled={preselectedTransaction !== null}
          validation={renderValidation(
            validationMessage(Field.RelatedTransaction, { required: translate('error') }),
          )}
        />
      )}
    />
  )
}

export default TransactionSelect
